import { css } from "@emotion/react";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { authFetch } from "../../authFetch";
import { prependBasename } from "../../utils/routingUtils";

export interface ScanRecord {
  scan_id: string;
  project_id?: string;
  plugin_type?: string;
  target_name?: string;
  target_id?: string | number;
  created_at?: string;
  total?: number;
  passed?: number;
  failed?: number;
  errors?: number;
  score?: number;
  status?: string;
  summary?: any;
}

const pageContainer = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  padding: 24px 32px 48px 32px;
  background-color: #f8fafc;
  box-sizing: border-box;
  font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
`;

const headerContainer = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;
`;

const titleArea = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const titleText = css`
  margin: 0;
  font-size: 1.55rem;
  font-weight: 850;
  color: #0f172a;
  letter-spacing: -0.02em;
`;

const subtitleText = css`
  margin: 0;
  font-size: 0.88rem;
  color: #64748b;
  font-weight: 500;
`;

const headerActions = css`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const outlineBtn = css`
  height: 36px;
  padding: 0 14px;
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 700;
  color: #334155;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;

  &:hover {
    background-color: #f8fafc;
    border-color: #94a3b8;
    color: #0f172a;
  }
`;

const primaryBtn = css`
  height: 36px;
  padding: 0 16px;
  background-color: #059669;
  border: 1px solid #059669;
  border-radius: 8px;
  font-size: 0.84rem;
  font-weight: 750;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 1px 2px rgba(5, 150, 105, 0.2);
  transition: all 0.15s ease;

  &:hover {
    background-color: #047857;
    border-color: #047857;
  }
`;

const metricsGrid = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const metricCard = css`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px 18px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.03);
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const metricLabel = css`
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
`;

const metricValue = css`
  font-size: 1.5rem;
  font-weight: 850;
  color: #0f172a;
`;

const filterBar = css`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const searchInput = css`
  flex: 1;
  min-width: 240px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.84rem;
  color: #0f172a;
  background-color: #ffffff;

  &:focus {
    outline: none;
    border-color: #059669;
  }
`;

const tableWrapper = css`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.03);
`;

const tableStyle = css`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 0.84rem;

  th {
    background-color: #f8fafc;
    color: #475569;
    font-weight: 750;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 12px 16px;
    border-bottom: 1px solid #e2e8f0;
  }

  td {
    padding: 14px 16px;
    border-bottom: 1px solid #f1f5f9;
    color: #1e293b;
    vertical-align: middle;
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:hover td {
    background-color: #f8fafc;
  }
`;

const statusBadge = (status: string) => {
  const s = status.toLowerCase();
  if (s === "completed" || s === "pass" || s === "success") {
    return css`
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 3px 9px;
      border-radius: 9999px;
      background-color: #ecfdf5;
      color: #059669;
      font-size: 0.72rem;
      font-weight: 750;
    `;
  }
  if (s === "failed" || s === "fail" || s === "error" || s.includes("findings")) {
    return css`
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 3px 9px;
      border-radius: 9999px;
      background-color: #fef2f2;
      color: #dc2626;
      font-size: 0.72rem;
      font-weight: 750;
    `;
  }
  return css`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: 9999px;
    background-color: #eff6ff;
    color: #2563eb;
    font-size: 0.72rem;
    font-weight: 750;
  `;
};

const emptyStateContainer = css`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 56px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 14px;
`;

export function ScanHistoryPage() {
  const navigate = useNavigate();
  const [scans, setScans] = useState<ScanRecord[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const fetchScans = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const res = await authFetch(prependBasename("/v1/security/targets/evaluations/list-scans"));
      if (res.ok) {
        const data = await res.json();
        setScans(Array.isArray(data) ? data : []);
      } else {
        setErrorMessage(`Failed to retrieve scans from server (HTTP ${res.status})`);
      }
    } catch (e: any) {
      setErrorMessage(e?.message || "Failed to connect to security evaluation service");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScans();
  }, [fetchScans]);

  const handleDownloadReport = async (scanId: string) => {
    try {
      const res = await authFetch(prependBasename(`/v1/security/targets/evaluations/scan/${scanId}/report.html`));
      if (res.ok) {
        const html = await res.text();
        const blob = new Blob([html], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `security-report-${scanId}.html`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error("Failed to download HTML report", e);
    }
  };

  const filteredScans = useMemo(() => {
    return scans.filter((s) => {
      if (statusFilter !== "all") {
        const st = (s.status || (s.failed && s.failed > 0 ? "failed" : "completed")).toLowerCase();
        if (statusFilter === "completed" && st !== "completed") return false;
        if (statusFilter === "failed" && st !== "failed") return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchId = (s.scan_id || "").toLowerCase().includes(q);
        const matchName = (s.target_name || "").toLowerCase().includes(q);
        const matchPlugin = (s.plugin_type || "").toLowerCase().includes(q);
        if (!matchId && !matchName && !matchPlugin) return false;
      }
      return true;
    });
  }, [scans, searchQuery, statusFilter]);

  const totalScans = scans.length;
  const completedScans = scans.filter((s) => !s.failed || s.failed === 0).length;
  const totalProbes = scans.reduce((acc, s) => acc + (s.total || 0), 0);
  const avgScore = totalScans > 0
    ? Math.round(scans.reduce((acc, s) => acc + (s.score ?? 100), 0) / totalScans)
    : 100;

  return (
    <div css={pageContainer} data-testid="scan-history-page">
      {/* Top Header */}
      <div css={headerContainer}>
        <div css={titleArea}>
          <h1 css={titleText}>Scan History</h1>
          <p css={subtitleText}>
            Audit log and execution history of active and passive red teaming scans.
          </p>
        </div>
        <div css={headerActions}>
          <button type="button" css={outlineBtn} onClick={fetchScans} disabled={isLoading}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
            </svg>
            <span>Refresh</span>
          </button>
          <button type="button" css={primaryBtn} onClick={() => navigate("/red-teaming")}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span>New Active Scan</span>
          </button>
        </div>
      </div>

      {/* Metric Cards */}
      <div css={metricsGrid}>
        <div css={metricCard}>
          <span css={metricLabel}>Total Scans</span>
          <span css={metricValue}>{totalScans}</span>
        </div>
        <div css={metricCard}>
          <span css={metricLabel}>Scans Passing</span>
          <span css={metricValue} style={{ color: "#059669" }}>{completedScans}</span>
        </div>
        <div css={metricCard}>
          <span css={metricLabel}>Total Probes Run</span>
          <span css={metricValue}>{totalProbes}</span>
        </div>
        <div css={metricCard}>
          <span css={metricLabel}>Avg Security Posture</span>
          <span css={metricValue} style={{ color: avgScore >= 80 ? "#059669" : avgScore >= 50 ? "#d97706" : "#dc2626" }}>
            {avgScore}%
          </span>
        </div>
      </div>

      {/* Filter Bar */}
      <div css={filterBar}>
        <input
          type="text"
          placeholder="Filter by scan ID, target name, or attack vector..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          css={searchInput}
        />
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "#64748b" }}>Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              height: "36px",
              padding: "0 10px",
              borderRadius: "6px",
              border: "1px solid #cbd5e1",
              fontSize: "0.82rem",
              fontWeight: 700,
              color: "#334155",
              backgroundColor: "#ffffff",
            }}
          >
            <option value="all">All Scans</option>
            <option value="completed">Completed / Passed</option>
            <option value="failed">Findings Detected</option>
          </select>
        </div>
      </div>

      {/* Content Area */}
      {isLoading ? (
        <div style={{ padding: "48px 0", textAlign: "center", color: "#64748b", fontSize: "0.9rem" }}>
          Loading scan history from backend...
        </div>
      ) : errorMessage ? (
        <div style={{ padding: "24px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", color: "#991b1b", fontSize: "0.88rem" }}>
          <strong>Error loading history:</strong> {errorMessage}
        </div>
      ) : filteredScans.length === 0 ? (
        <div css={emptyStateContainer}>
          <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "#ecfdf5", color: "#059669", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.5rem" }}>
            🛡️
          </div>
          <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#0f172a" }}>
            {scans.length === 0 ? "No Scans Recorded Yet" : "No Matching Scans Found"}
          </h3>
          <p style={{ margin: 0, fontSize: "0.85rem", color: "#64748b", maxWidth: "480px" }}>
            {scans.length === 0
              ? "When you launch an active or passive security scan in Red Teaming, the scan execution record and evaluation results will appear here."
              : "Try adjusting your search query or status filter to see other scan records."}
          </p>
          <button type="button" css={primaryBtn} onClick={() => navigate("/red-teaming")} style={{ marginTop: "8px" }}>
            Launch Active Scan
          </button>
        </div>
      ) : (
        <div css={tableWrapper}>
          <table css={tableStyle}>
            <thead>
              <tr>
                <th>Scan ID</th>
                <th>Target / Application</th>
                <th>Started At</th>
                <th>Probes</th>
                <th>Security Score</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredScans.map((scan) => {
                const targetName = scan.target_name || scan.plugin_type || "Security Target";
                const total = scan.total ?? 0;
                const passed = scan.passed ?? 0;
                const failed = scan.failed ?? 0;
                const score = scan.score ?? (total > 0 ? Math.round((passed / total) * 100) : 100);
                const status = scan.status || (failed > 0 ? "Findings Detected" : "Completed");
                const timeStr = scan.created_at ? new Date(scan.created_at).toLocaleString() : "Recent";

                return (
                  <tr key={scan.scan_id}>
                    <td>
                      <button
                        type="button"
                        onClick={() => navigate(`/red-teaming?scanId=${encodeURIComponent(scan.scan_id)}`)}
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          color: "#2563eb",
                          fontFamily: "ui-monospace, monospace",
                          fontWeight: 700,
                          fontSize: "0.82rem",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        {scan.scan_id}
                      </button>
                    </td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span style={{ fontWeight: 750, color: "#0f172a" }}>{targetName}</span>
                        {scan.plugin_type && scan.plugin_type !== targetName && (
                          <span style={{ fontSize: "0.72rem", color: "#64748b" }}>{scan.plugin_type}</span>
                        )}
                      </div>
                    </td>
                    <td style={{ color: "#475569", whiteSpace: "nowrap" }}>{timeStr}</td>
                    <td>
                      <span style={{ fontWeight: 700 }}>{passed}</span> / {total} passed
                      {failed > 0 && <span style={{ color: "#dc2626", marginLeft: "6px", fontWeight: 700 }}>({failed} failed)</span>}
                    </td>
                    <td>
                      <span
                        style={{
                          fontWeight: 800,
                          color: score >= 80 ? "#059669" : score >= 50 ? "#d97706" : "#dc2626",
                        }}
                      >
                        {score}%
                      </span>
                    </td>
                    <td>
                      <span css={statusBadge(status)}>
                        <span style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "currentColor" }} />
                        {status}
                      </span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <div style={{ display: "inline-flex", gap: "8px" }}>
                        <button
                          type="button"
                          css={outlineBtn}
                          style={{ height: "30px", padding: "0 10px", fontSize: "0.75rem" }}
                          onClick={() => navigate(`/red-teaming?scanId=${encodeURIComponent(scan.scan_id)}`)}
                        >
                          View Report
                        </button>
                        <button
                          type="button"
                          css={outlineBtn}
                          style={{ height: "30px", padding: "0 8px", fontSize: "0.75rem" }}
                          title="Download Standalone HTML Report"
                          onClick={() => handleDownloadReport(scan.scan_id)}
                        >
                          HTML
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
export default ScanHistoryPage;
